import { useState } from 'react'
import { Form, Formik, FormikState } from 'formik'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material'
import {
  useGetDtsPlace,
  usePlace,
  usePatchPlace,
  usePatchDtsPlace,
} from 'hooks/usePlaces'
import { SaveBtn } from 'components/SaveBtn/SaveBtn'
import { useAlert } from 'providers/AlertProvider'
import GoBackButton from 'components/GoBackButton/GoBackButton'
import Input from 'components/Input/Input'
import LoadingIndicator from 'components/LoadingIndicator/LoadingIndicator'
import { ResponsiveBoxWrapper } from 'components/Layout/Layout.styled'
import { PlaceSecondarySidebar } from './PlaceSecondarySidebar'

type PlaceContactFormProps = {
  email: string
  phone: string
  supportEmail: string
  supportPhone: string
}

export const PlaceContact = () => {
  const { t } = useTranslation()
  const { placeId } = useParams()
  const {
    isLoading: isLoadingPlace,
    error: placeError,
    place,
  } = usePlace(placeId as string)
  const { isLoading, error, dtsPlace } = useGetDtsPlace(place.masterId)
  const { mutateAsync, isLoading: isLoadingRequest } = usePatchPlace(
    placeId as string
  )
  const { mutateAsync: mutateAsyncDts, isLoading: isLoadingDtsRequest } =
    usePatchDtsPlace(place.masterId as string)

  const { setAlert } = useAlert()
  const [showOverlay, setShowOverlay] = useState(true)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const initialValues = {
    email: place.email,
    phone: place.phones[0]?.number || '',
    supportEmail: dtsPlace.supportEmail,
    supportPhone: dtsPlace.supportPhone,
  }

  const validationSchema = yup.object({
    email: yup
      .string()
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, t('placeContact.validEmail')),
    phone: yup.string(),
    supportEmail: yup.string(),
    supportPhone: yup.string(),
  })

  const handleSubmit = async (
    values: PlaceContactFormProps,
    resetForm: (nextState?: Partial<FormikState<PlaceContactFormProps>>) => void
  ) => {
    const placeValues = {
      email: values.email,
      phones: values.phone
        ? [{ ...place.phones[0], number: values.phone }]
        : [],
    }
    const placeDtsValues = {
      support_email: values.supportEmail,
      support_phone: values.supportPhone,
      // back end requires store_guid for this request
      store_guid: dtsPlace.guid,
    }
    await mutateAsync(placeValues, {
      onSuccess: () => {
        mutateAsyncDts(placeDtsValues, {
          onSuccess: () => {
            setAlert(t('saved'), 'success', {
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'left',
            })
            resetForm({ values })
          },
          onError: () => {
            setAlert(t('failureMessage'), 'failure', {
              vertical: 'bottom',
              horizontal: isMobile ? 'center' : 'left',
            })
          },
        })
      },
      onError: () => {
        setAlert(t('failureMessage'), 'failure', {
          vertical: 'bottom',
          horizontal: isMobile ? 'center' : 'left',
        })
      },
    })
  }

  if (isLoading || !dtsPlace || isLoadingPlace || !place)
    return <LoadingIndicator />
  if (error || placeError) return <h1>Error</h1>
  return (
    <Box sx={{ display: 'flex' }}>
      <PlaceSecondarySidebar
        placeId={placeId as string}
        placeName={place.name}
        placeMasterId={place.masterId}
      />
      <ResponsiveBoxWrapper showOverlay={showOverlay}>
        <Box maxWidth='500px'>
          {isMobile && <GoBackButton onClick={() => setShowOverlay(false)} />}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) =>
              handleSubmit(values, resetForm)
            }>
            {({ isValid, errors, dirty }) => (
              <Form>
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='space-between'>
                  {place && dtsPlace && (
                    <Box
                      display='flex'
                      flexDirection='column'
                      justifyContent='space-around'
                      mt={theme.spacing(3)}>
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(4),
                          marginTop: theme.spacing(1),
                        }}>
                        {t('placeContact.general')}
                      </Typography>
                      <Input
                        errorMessage={errors.email as string | undefined}
                        label={t('placeContact.email')}
                        name='email'
                        type='text'
                        variant='filled'
                        fullWidth
                      />
                      <Input
                        errorMessage={errors.phone as string | undefined}
                        label={t('placeContact.phoneNumber')}
                        name='phone'
                        type='text'
                        variant='filled'
                        fullWidth
                      />
                      <Typography
                        variant='body2'
                        sx={{
                          marginBottom: theme.spacing(4),
                          marginTop: theme.spacing(1),
                        }}>
                        {t('placeContact.support')}
                      </Typography>
                      <Input
                        errorMessage={errors.supportEmail as string | undefined}
                        label={t('placeContact.supportEmail')}
                        name='supportEmail'
                        type='text'
                        variant='filled'
                      />
                      <Input
                        disabled
                        errorMessage={errors.supportPhone as string | undefined}
                        label={t('placeContact.supportPhone')}
                        name='supportPhone'
                        type='text'
                        variant='filled'
                      />
                    </Box>
                  )}
                  <Box
                    sx={{
                      position: 'fixed',
                      bottom: isMobile ? '0px' : theme.spacing(2),
                      width: isMobile ? '92%' : '504px',
                      zIndex: 1000,
                    }}>
                    <SaveBtn
                      showOverlay={showOverlay}
                      isValid={isValid}
                      dirty={dirty}
                      isLoadingRequest={isLoadingRequest || isLoadingDtsRequest}
                    />
                  </Box>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </ResponsiveBoxWrapper>
    </Box>
  )
}
